import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormRecord,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  CountryProduct,
  CountryProductService,
} from 'src/app/services/country-product/country-product.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  CreateInfoNotificationRequest,
  InfoNotification,
  InfoNotificationsService,
  LangData,
  Process,
} from 'src/app/services/info-notifications/info-notifications.service';
import {
  ActionGroupId,
  actionGroups,
} from 'src/app/shared/payee-cases/types';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../../shared/payee-input/payee-input.directive';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeDropdownComponent } from '../../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeIconButtonComponent } from '../../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatCheckbox } from '@angular/material/checkbox';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-create-info-notification-dialog',
  templateUrl: './create-info-notification-dialog.component.html',
  styleUrls: ['./create-info-notification-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    PayeeAutocompleteComponent,
    PayeeDropdownComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatCheckbox,
    PayeeButtonComponent,
    MatExpansionPanelTitle,
    TranslateModule,
    MatProgressSpinner,
  ],
})
export class CreateInfoNotificationDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<
      MatDialogRef<
        CreateInfoNotificationDialogComponent,
        InfoNotification | null
      >
    >(MatDialogRef);
  private infoNotificationsService = inject(InfoNotificationsService);
  private helpersService = inject(HelpersService);
  private countryProductService = inject(CountryProductService);

  readonly form = this.fb.group({
    name: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    transition_group: this.fb.control<ActionGroupId>(
      'operation',
      {
        validators: Validators.required,
        nonNullable: true,
      },
    ),
    langs: new FormRecord<ReturnType<typeof this.createLangGroup>>(
      {},
      { validators: Validators.required },
    ),
  });

  readonly transitionGroupOptions = [...actionGroups];

  loading = false;
  dataReady = false;
  errorMessage = '';

  countries: CountryProduct[] = [];
  readonly selectedLanguage = this.fb.control<CountryProduct | null>(null);

  processes: Process[] = [];
  selectedProcessStates = new Set<string>();

  readonly transitionGroupDisplayWith = (group: ActionGroupId): string => {
    switch (group) {
      case 'filing':
        return 'Beérkező iratok';
      case 'submission':
        return 'Beadványok';
      case 'payment':
        return 'Kifizetettség kezelés';
      default:
        return 'Ügyintézés';
    }
  };

  get langs() {
    return this.form.controls.langs as FormRecord<
      ReturnType<typeof this.createLangGroup>
    >;
  }
  get langForm(): ReturnType<typeof this.createLangGroup> | undefined {
    const iso = this.selectedLanguage.value?.iso;
    if (!iso) {
      return undefined;
    }
    return this.langs.controls[iso];
  }
  get log_label() {
    return this.langForm?.controls.log_label;
  }
  get subject() {
    return this.langForm?.controls.subject;
  }
  get title() {
    return this.langForm?.controls.title;
  }
  get message() {
    return this.langForm?.controls.message;
  }
  get is_containing_attachment() {
    return this.langForm?.controls.is_containing_attachment;
  }

  get addedLanguages(): string[] {
    return Object.keys(this.langs.controls);
  }

  getLangForm(iso: string): ReturnType<typeof this.createLangGroup> | undefined {
    return this.langs.controls[iso];
  }

  async ngOnInit(): Promise<void> {
    try {
      const [countries, processes] = await Promise.all([
        this.countryProductService.getCountries(),
        this.infoNotificationsService.getAllProcessStates(),
      ]);
      this.countries = countries;
      this.processes = processes;
      this.dataReady = true;
    } catch (error) {
      console.error('Error fetching data:', error);
      this.errorMessage = 'Hiba történt az adatok betöltése során.';
    }
  }

  addLang(): void {
    const selectedLanguage = this.selectedLanguage.value;
    if (selectedLanguage) {
      this.langs.setControl(selectedLanguage.iso, this.createLangGroup());
    }
  }

  selectLang(lang: string): void {
    const country = this.countries.find((c) => c.iso === lang);
    this.selectedLanguage.patchValue(country ?? null);
  }

  removeLang(lang: string): void {
    this.langs.removeControl(lang);
    if (
      this.addedLanguages.length > 0 &&
      this.selectedLanguage.value?.iso === lang
    ) {
      this.selectLang(this.addedLanguages[0]);
    }
  }

  countryDisplayWith = (country: CountryProduct | null): string => {
    return country ? country.name : '';
  };

  toggleProcessState(stateClass: string): void {
    if (this.selectedProcessStates.has(stateClass)) {
      this.selectedProcessStates.delete(stateClass);
    } else {
      this.selectedProcessStates.add(stateClass);
    }
  }

  isProcessStateSelected(stateClass: string): boolean {
    return this.selectedProcessStates.has(stateClass);
  }

  getSelectedStatesCount(process: Process): number {
    return process.states.filter((state) =>
      this.selectedProcessStates.has(state.class),
    ).length;
  }

  readonly onSubmit = async () => {
    if (this.form.invalid || this.loading || this.addedLanguages.length === 0) {
      if (this.addedLanguages.length === 0) {
        this.errorMessage = 'Legalább egy nyelvet hozzá kell adni.';
        return;
      }
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    if (this.selectedProcessStates.size === 0) {
      this.errorMessage = 'Legalább egy folyamat állapotot ki kell választani.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const { name, transition_group, langs } = this.form.getRawValue();

    const createRequest: CreateInfoNotificationRequest = {
      name,
      transition_group,
      langs: langs as Record<string, LangData>,
      process_states: Array.from(this.selectedProcessStates),
    };

    try {
      const newNotification =
        await this.infoNotificationsService.createInfoNotification(
          createRequest,
        );

      this.dialogRef.close(newNotification);
    } catch (error) {
      console.error('Error creating notification:', error);
      this.errorMessage =
        'Hiba történt a létrehozás során. Kérjük, próbálja újra.';
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = () => {
    this.dialogRef.close();
  };

  private createLangGroup() {
    return this.fb.group({
      log_label: this.fb.control('', {
        validators: Validators.required,
        nonNullable: true,
      }),
      subject: this.fb.control('', {
        validators: Validators.required,
        nonNullable: true,
      }),
      title: this.fb.control('', {
        validators: Validators.required,
        nonNullable: true,
      }),
      message: this.fb.control('', {
        validators: Validators.required,
        nonNullable: true,
      }),
      is_containing_attachment: this.fb.control(false, { nonNullable: true }),
    });
  }
}
